import { PricingModel } from '@ekhodealer/ekho-common';
import { Card, Group, Note,Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { EnrichedPlanDetails} from '@ekhodealer/ekho-common';
import type { ReactNode } from 'react';

import { formatNumberWithCommas } from '@common/lib/currency';
import { capitalizeFirstLetter } from '@common/lib/strings';

import { useGetPlanDetails } from '../api/useGetPlanDetails';

interface PlanSummaryNoteProps {
	label: string;
	value: ReactNode;
}

function PlanSummaryNote({ label, value }: PlanSummaryNoteProps) {
	return (
		<Note>
			<Stack itemsAlignX='start'>
				<Text size='footnote' weight='semibold' align='left'>
					{label}
				</Text>
				<Text size='bodySmall' weight='semibold' align='left'>
					{value}
				</Text>
			</Stack>
		</Note>
	);
}

function PlanSummary() {
	const { data: planDetails } = useGetPlanDetails();

	function formatFrequency(planDetails: EnrichedPlanDetails | object): string {
		const { reloadFrequency } = planDetails as EnrichedPlanDetails;
		if (!reloadFrequency) {
			return '';
		}

		switch (reloadFrequency) {
			case 'monthly':
				return 'Month';
			case 'quarterly':
				return 'Quarter';
			case 'biannually':
				return 'Half-year';
			case 'annually':
				return 'Year';
			default:
				return '';
		}
	}

	function formatModel(model: string) {
		switch (model) {
			case PricingModel.TIERED:
				return 'Tiered';
			case PricingModel.FLAT_FEE:
				return 'Flat fee';
			default:
				return 'Fixed'; // fallback to fixed
		}
	}

	return (
		<Card>
			<Stack itemsSpacing='16'>
				<Stack itemsSpacing='8'>
					<Text size='body' weight='semibold'>
						Plan summary
					</Text>
					<Stack itemsSpacing='4'>
						<Text size='bodySmall'>
							• Your plan includes{' '}
							<b>${formatNumberWithCommas(planDetails?.reloadAmount || 0)}</b> in
							credits every <b>{formatFrequency(planDetails || {})}</b>.
						</Text>
						<Text size='bodySmall'>
							• Your per-sale fee is <b>{planDetails?.rate}</b>.
						</Text>
						<Text size='bodySmall'>
							• Credits <b>reset {planDetails?.expiryFrequency} </b> and don't carry
							over.
						</Text>
						<Text size='bodySmall'>
							• If your credit balance runs out, fees are <b> deducted per sale.</b>
						</Text>
					</Stack>
				</Stack>
				<Stack itemsSpacing='16'>
					<Group noWrap itemsSpacing='16'>
						<PlanSummaryNote
							label='Plan model'
							value={capitalizeFirstLetter(planDetails?.contractType || '')}
						/>
						<PlanSummaryNote
							label='Term'
							value={
								<>
									{formatFrequency(planDetails || {})}
									(renews {planDetails?.nextReloadDate})
								</>
							}
						/>
					</Group>
					<Group noWrap itemsSpacing='16'>
						<PlanSummaryNote
							label='Per-sale method'
							value={formatModel(planDetails?.model || '')}
						/>
						<PlanSummaryNote
							label='Next credit reset'
							value={planDetails?.nextExpiryDate}
						/>
					</Group>
				</Stack>
			</Stack>
		</Card>
	);
}

export default PlanSummary;
